<script lang="ts" setup>
import { useAuth, useDialog, useDraw, useTemplateRef, useRouter } from "#imports";
import { onClickOutside } from '@vueuse/core'
const { currentUser, signOut } = useAuth();
const { toggleDraw } = useDraw();
const { openDialog, setDialogFullscreen } = useDialog();

const target = useTemplateRef<HTMLElement>('target')
onClickOutside(target, () => toggleDraw())

const handleGetPremiumClick = () => {
  openDialog('MPPricing');
  setDialogFullscreen(true)
};

const router = useRouter();
const menuItems = [
  {
    label: 'Settings',
    id: 'settings',
    showLabel: false,
    withDivider: true,
    items: [
      {
        label: 'Account',
        action: () => router.push('/account')
      },
      {
        label: 'Help & Support',
        action:  () => {
          location.href = 'mailto:support@mappr.com';
        }
      },
      {
        label: 'Creator Studio',
        creatorsOnly: true,
        action: () => router.push('/studio')
      }
    ]
  },
  {
    label: 'Supporting',
    id: 'supporting',
    showLabel: true,
    withDivider: false,
    withSlot: true,
  },
  // {
  //   label: 'Following',
  //   id: 'following',
  //   showLabel: false,
  //   withSlot: true,
  // }
];
</script>

<template>
  <div class="draw" ref="target">
    <button class="close-button" @click="toggleDraw">
      <i class="ri-close-line"/>
    </button>
    <div class="user-info">
      <MPImg :src="currentUser?.profile?.profile_image_url || '/media/images/placeholder-user.jpg'" class="user-image" :width="65" :height="65" />
      <div>
        <h1>{{ currentUser?.profile?.mappr_handle || "Your account" }}</h1>
        <span>{{ currentUser?.user?.email }}</span>
      </div>
    </div>
    <MPMenu :menu-items="menuItems">
      <template #['supporting']>
        <span v-if="currentUser?.supports?.length === 0">You are not supporting any creators</span>
        <ul v-else>
          <li v-for="creator in currentUser?.supports" :key="creator.id">
            <NuxtLinkLocale :to="`/@${creator.profiles?.mappr_handle}`">
              <MPImg :src="creator?.profiles?.profile_image_url || '/media/images/placeholder-user.jpg'" :width="35" :height="35" />
              @{{ creator?.profiles?.mappr_handle }}
            </NuxtLinkLocale>
          </li>
        </ul>
      </template>
      <!-- <template #['following']>
        <span v-if="currentUser?.supports?.length === 0">You are not supporting any creators</span>
        <ul v-else>
          <li v-for="creator in currentUser?.supports" :key="creator.id">
            <NuxtLinkLocale :to="`/@${creator.profiles?.mappr_handle}`">
              <MPImg :src="creator?.profiles?.profile_image_url || '/media/images/placeholder-user.jpg'" :width="35" :height="35" />
              @{{ creator?.profiles?.mappr_handle }}
            </NuxtLinkLocale>
          </li>
        </ul>
      </template> -->
    </MPMenu>
    <!-- <nav>
      <h2>Following</h2>
      <span>You are not following any creators</span>
      <ul v-else>
        <li v-for="creator in currentUser?.following" :key="creator.id">
          @{{ creator?.profiles?.mappr_handle }}
        </li>
      </ul>
    </nav> -->

    <nav class="actions">
      <a @click="handleGetPremiumClick" class="premium-link">
        <span>Get Premium</span>
        <i class="ri-sparkling-line"/>
      </a>
      <NuxtLinkLocale @click="signOut" style="cursor:pointer">
        Sign out
      </NuxtLinkLocale>
    </nav>
  </div>
</template>

<style lang="postcss" scoped>
@import "assets/css/typography.pcss";
@import "assets/css/breakpoints.pcss";

.draw {
  position: fixed;
  display: flex;
  flex-direction: column;
  top: 0;
  right: 0;
  width: 20rem;
  height: 100dvh;
  background-color: var(--color-slate-800);
  z-index: 8002;
  color: var(--color-slate-50);
  padding: 0.65rem;
}

.close-button {
  position: absolute;
  top: 0.5rem;
  right: 0.25rem;
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-slate-50);
  font-size: 1.65rem;
}

.premium-link {
  display: grid;
  cursor: pointer;
  position: relative;
  margin: 0;
  padding: 0.5rem 1.25rem;
  background-color: var(--color-slate-100);
  color: var(--color-slate-900);
  border-radius: 1rem;
}

.premium-link i {
  color: var(--color-yellow-400) !important;
  position: absolute;
  font-size: 1.25rem;
  top: -0.6rem;
  right: -0.5rem;
}

.user-info {
  display: flex;
  padding: 1rem;
  align-items: top;
  gap: 0.75rem;
}

.user-info > span {
  font-size: 1.25rem;
  margin: 0;
  padding: 0 0 0.5rem 0;
  line-height: 1;
}

.user-info h1 {
  font-size: 1.25rem;
  margin: 0;
  padding: 0.25rem 0 0;
}

.user-info div {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.user-image {
  border-radius: 100%;
  object-fit: cover;
  width: 60px;
  height: 60px;
}

nav {
  padding: 0 1rem;
}
nav h2 {
  font-size: 1rem;
  color: var(--color-slate-400);
}

nav + nav {
  padding-top: 1rem;
}

nav ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

nav ul li a {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

nav ul li img {
  width: 35px;
  height: 35px;
  object-fit: cover;
  border-radius: 100%;
}

nav.actions {
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  font-size: 1rem;
}

nav.actions i {
  font-size: 1.65rem;
  color: var(--color-slate-50);
}

nav.actions a {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  color: var(--color-slate-400);
}

.studio {
  position: relative;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  color: var(--color-slate-100);
}

.studio i {
  font-size: 2rem;
}
</style>
